// 🔑 A HELD GUIDED RUN, CLEARED HERE (Jesse, 2026-10-26 — backend utils/checkFailures). The step-5 check, step-7 pull or
// step-8 decision failed twice in a row: the borrower's retry is held and engineering has a ticket. Once fixed, staff
// say what was fixed and clear it — the borrower is emailed once and their button works again (or run the check as
// staff: a successful staff re-run clears it too). Recent failures show their cause and log reference.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API, B, stamp } from './parts';

const LABEL = { step5_check: 'Step-5 document check', step7_pull: 'Step-7 credit pull', step8_decision: 'Step-8 decision' };

export default function GuidedCheckHolds({ borrowerId, token, kinds }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/admin/borrowers/${borrowerId}/guided-check-holds`, { headers }).catch(() => null);
    setData(r ? r.data : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId, token]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const state = (data.state || []).filter((s) => !kinds || kinds.includes(s.kind));
  const recent = (data.recent || []).filter((f) => !kinds || kinds.includes(f.kind));
  if (!state.some((s) => s.held || s.consecutive_failures > 0) && !recent.length) return null;
  const clear = async (kind) => {
    setBusy(kind); setMsg(null);
    try {
      const r = await axios.post(`${API}/api/admin/borrowers/${borrowerId}/guided-check-holds/clear`, { kind, reason: reason[kind] || '' }, { headers });
      setMsg(r.data.emailed ? 'Cleared. The borrower has been emailed that it is fixed.' : 'Cleared.');
      await load();
    } catch (e) { setMsg((e && e.response && e.response.data && e.response.data.error) || 'Could not clear the hold.'); }
    finally { setBusy(null); }
  };
  return (
    <div data-testid="guided-check-holds" style={{ border: `1px solid ${B.line}`, borderLeft: `4px solid ${B.red}`, borderRadius: 10, padding: 12, display: 'grid', gap: 8, color: B.ink, fontSize: 13 }}>
      <div style={{ fontWeight: 800 }}>Failed checks</div>
      {state.map((s) => (
        <div key={s.kind} data-testid="guided-check-hold" data-kind={s.kind} data-held={s.held ? 'true' : 'false'} style={{ display: 'grid', gap: 6 }}>
          <div>
            <strong>{LABEL[s.kind] || s.kind}</strong>{' — '}
            {s.held ? `HELD since ${stamp(s.held_at)}${s.ticket_number ? ` · ticket ${s.ticket_number}` : ''}` : `${s.consecutive_failures} failure(s) in a row`}
            {s.last_cause_code ? ` · last cause ${s.last_cause_code}` : ''}
          </div>
          {s.held && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input data-testid="guided-check-hold-reason" placeholder="What was fixed?" value={reason[s.kind] || ''}
                onChange={(e) => setReason({ ...reason, [s.kind]: e.target.value })}
                style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13, padding: 7, borderRadius: 6, border: `1px solid ${B.slate}` }} />
              <button type="button" data-testid="guided-check-hold-clear" disabled={busy === s.kind || String(reason[s.kind] || '').trim().length < 5}
                onClick={() => clear(s.kind)}
                style={{ padding: '7px 12px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: B.blue, color: B.white, cursor: 'pointer' }}>
                {busy === s.kind ? 'Clearing…' : 'Clear and email the borrower'}
              </button>
            </div>
          )}
        </div>
      ))}
      {recent.length > 0 && (
        <div style={{ display: 'grid', gap: 3 }}>
          {recent.map((f, i) => (
            <div key={i} style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
              {`${stamp(f.created_at)} · ${LABEL[f.kind] || f.kind} · ${f.cause_code}: ${f.cause_text || ''} · ref ${f.log_ref || '—'}`}
            </div>
          ))}
        </div>
      )}
      {msg && <div role="status" style={{ fontSize: 12.5 }}>{msg}</div>}
    </div>
  );
}
